import { LocalStorageService } from "./LocalStorageService";
/** Authentication namespace
 * @namespace
 */
export const Auth = {
	isLoggedIn: false,
	token: null,
	userName: null,
	refreshToken: null,

	/**
	 * Logical session generation — a monotonic counter of REAL session-identity
	 * transitions (a new login / authorize / SSO storage replacement, or a session end).
	 * Token rotation INSIDE one session (the /token refresh grant) deliberately does NOT
	 * advance it. Async auth work captures this value BEFORE its async work and
	 * revalidates before applying results (src/actions/UserActions.js refreshToken),
	 * and src/services/AxiosMiddleware.js stamps it on every outgoing request as
	 * frontend-only config metadata (config.authSession), so completions left over
	 * from a REPLACED session can be recognized and settled without ever touching the
	 * new session's credentials.
	 */
	sessionGeneration: 0,

	/** Adopt a stored authorizationData payload into the cached singleton fields. */
	_applyAuthData(authData) {
		this.isLoggedIn = true;
		this.token = authData.access_token;
		this.refreshToken = authData.refresh_token;
	},

	/** Drop the cached singleton credentials (storage stays owned by the caller). */
	_clearCachedAuthData() {
		this.isLoggedIn = false;
		this.token = null;
		this.refreshToken = null;
	},

	/** True when a cached session exists (a real transition can end it). */
	_hasCachedSession() {
		return Boolean(this.isLoggedIn || this.token || this.refreshToken);
	},

	/**
	 * Synchronize the cached identity with the stored authorizationData. Storage is the
	 * source of truth — LOGIN/AUTHORIZE and the OTHER application's SSO writer may
	 * replace it raw (src/actions/UserActions.js loginUser/authorize,
	 * src/index.js's message listener): a DIFFERENT stored credential identity is a NEW
	 * logical session and advances the generation exactly once; a missing stored session
	 * clears the cached credentials (the post-logout token-reuse fix) and advances only
	 * when a real session actually ended. Reads of the SAME session never advance
	 * anything (route guards call isAuthenticated on every mount — an advance per call
	 * would orphan every queued refresh against its own session).
	 *
	 * Payload deserialization is NOT touched: LocalStorageService.get's JSON.parse
	 * behavior (including its throw on corrupt raw storage) stays exactly as it was.
	 */
	_syncSessionFromStorage(authData) {
		if (authData && authData.access_token) {
			if (this.token !== authData.access_token || this.refreshToken !== authData.refresh_token) {
				this._applyAuthData(authData);
				this.sessionGeneration += 1;
			}
			return true;
		}
		if (this._hasCachedSession()) {
			this._clearCachedAuthData();
			this.sessionGeneration += 1;
		}
		return false;
	},

	/** Check is user authenticated or not */
	isAuthenticated() {
		const authData = LocalStorageService.get("authorizationData");

		const authenticated = this._syncSessionFromStorage(authData);
		if (authenticated) {
			this.role = authData.superAdmin;
		}
		return authenticated;
	},

	/**
	 * @typedef AuthData
	 * @property {boolean} isLoggedIn Is user loged in or not
	 * @property {string} token Token
	 * @property {string} refreshToken Refresh token
	 */

	/** Get authenticated user's data
	 * @returns {AuthData} */

	fillAndGetData() {
		const authData = LocalStorageService.get("authorizationData");

		this._syncSessionFromStorage(authData);
		return this;
	},

	/** Get authenticated user's data
	 * @returns {AuthData} */

	/**
	 * Store a NEW logical session (login / authorize callback) — the payload format is
	 * preserved VERBATIM (the login/authorize wire payloads round-trip untouched); the
	 * cached identity is replaced and the session generation ADVANCES so async work
	 * still bound to the previous session can detect it is stale.
	 * @returns {AuthData} */

	setAuthData(authData) {
		LocalStorageService.set("authorizationData", authData);

		if (authData && authData.access_token) {
			this._applyAuthData(authData);
			this.sessionGeneration += 1;
		} else {
			this._syncSessionFromStorage(null);
		}
		return this;
	},

	/**
	 * Persist REFRESHED tokens for the CURRENT session (src/actions/UserActions.js
	 * refreshToken) — a token rotation INSIDE the same logical session: the cached
	 * identity updates but the session generation does NOT advance, so this session's
	 * requests and pending replays stay owned by it.
	 * @returns {AuthData} */

	adoptRefreshedAuthData(authData) {
		LocalStorageService.set("authorizationData", authData);

		if (authData && authData.access_token) {
			this._applyAuthData(authData);
		}
		return this;
	},

	/** Invalidate user session */
	invalidateSession() {
		let storedSessionExists = false;
		try {
			storedSessionExists = Boolean(LocalStorageService.get("authorizationData"));
		} catch {
			// Corrupt raw storage behaves like no session — the removes below still clear it.
		}
		const hadSession = this._hasCachedSession() || storedSessionExists;
		LocalStorageService.remove("authorizationData");
		LocalStorageService.remove("role");
		this._clearCachedAuthData();
		// Advance ONCE per real transition: repeated invalidations of an already-ended
		// session stay idle, so queues keyed to the current generation keep working.
		if (hadSession) {
			this.sessionGeneration += 1;
		}
		return this;
	},

	setRole(role) {
		LocalStorageService.set("role", role);
	},

	getRole() {
		return LocalStorageService.get("role");
	},

	// TODO change checking logic
	checkPermission() {
		return LocalStorageService.get("role");
	},
};
